import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { StatusPill } from '../ui/StatusPill';
import { CalendarIcon, ClockIcon, TagIcon, ChevronDownIcon, PackageIcon } from 'lucide-react';
import { Bid } from '../../data/bids';
interface BidCardProps {
  bid: Bid;
  index: number;
}
export function BidCard({
  bid,
  index
}: BidCardProps) {
  const [showLots, setShowLots] = useState(false);
  const [expandedLot, setExpandedLot] = useState<number | null>(null);
  const daysUntilDeadline = Math.ceil((new Date(bid.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.05
  }} data-id="element-26">
      <Card hover data-id="element-27">
        <div className="flex items-start justify-between mb-3" data-id="element-28">
          <span className="text-sm font-mono text-gray-500" data-id="element-29">
            {bid.reference}
          </span>
          <StatusPill status={bid.status} data-id="element-30">
            {bid.status === 'closing-soon' ? 'CLOSING SOON' : bid.status === 'open' ? 'OPEN' : 'CLOSED'}
          </StatusPill>
        </div>

        <h3 className="text-xl font-bold text-charcoal mb-3" data-id="element-31">{bid.title}</h3>

        <p className="text-gray-600 mb-4" data-id="element-32">{bid.description}</p>

        {bid.lots && bid.lots.length > 0 && <div className="mb-4" data-id="element-33">
            <button type="button" onClick={() => setShowLots(s => !s)} className="flex items-center gap-2 text-sm font-medium text-au-green hover:text-au-green-dark transition-colors" aria-expanded={showLots} data-id="element-34">
              <PackageIcon size={16} data-id="element-35" />
              <span data-id="element-36">
                {showLots ? 'Hide' : 'View'} lot breakdown ({bid.lots.length}{' '}
                lots)
              </span>
              <ChevronDownIcon size={16} className={`transition-transform ${showLots ? 'rotate-180' : ''}`} data-id="element-37" />
            </button>

            <AnimatePresence initial={false} data-id="element-38">
              {showLots && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.25
          }} className="overflow-hidden" data-id="element-39">
                  <div className="mt-3 space-y-2 border-l-2 border-au-gold pl-4" data-id="element-40">
                    {bid.lots.map((lot, i) => {
                const isOpen = expandedLot === i;
                return <div key={i} className="border border-gray-border rounded-lg bg-white" data-id="element-41">
                          <button type="button" onClick={() => setExpandedLot(isOpen ? null : i)} className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-gray-50 transition-colors rounded-lg" aria-expanded={isOpen} data-id="element-42">
                            <div className="min-w-0" data-id="element-43">
                              <p className="font-semibold text-charcoal text-sm" data-id="element-44">
                                {lot.name}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5" data-id="element-45">
                                {lot.summary}
                              </p>
                            </div>
                            <ChevronDownIcon size={16} className={`text-gray-400 flex-shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-46" />
                          </button>

                          <AnimatePresence initial={false} data-id="element-47">
                            {isOpen && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-48">
                                <div className="px-3 pb-3 pt-1 border-t border-gray-border" data-id="element-49">
                                  <p className="text-xs font-semibold text-charcoal uppercase tracking-wide mb-2 mt-2" data-id="element-50">
                                    Indicative Products
                                  </p>
                                  <ul className="text-sm text-gray-700 space-y-1 mb-3" data-id="element-51">
                                    {lot.products.map((p, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-52">
                                        <span className="text-au-green mt-0.5" data-id="element-53">
                                          •
                                        </span>
                                        <span data-id="element-54">{p}</span>
                                      </li>)}
                                  </ul>
                                  <p className="text-xs font-semibold text-charcoal uppercase tracking-wide mb-1" data-id="element-55">
                                    Use Cases
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-56">
                                    {lot.useCases}
                                  </p>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        <div className="space-y-2 mb-4" data-id="element-57">
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-58">
            <CalendarIcon size={16} data-id="element-59" />
            <span data-id="element-60">
              Published:{' '}
              {new Date(bid.publicationDate).toLocaleDateString('en-GB')}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm font-medium text-au-red" data-id="element-61">
            <ClockIcon size={16} data-id="element-62" />
            <span data-id="element-63">
              Deadline: {new Date(bid.deadline).toLocaleDateString('en-GB')}
            </span>
            {bid.status === 'open' && daysUntilDeadline > 0 && <span className="text-gray-500" data-id="element-64">
                ({daysUntilDeadline} days remaining)
              </span>}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-65">
            <TagIcon size={16} data-id="element-66" />
            <span data-id="element-67">{bid.category}</span>
          </div>
        </div>

        {bid.estimatedValue && <div className="pt-4 border-t border-gray-border" data-id="element-68">
            <div className="flex items-center justify-between" data-id="element-69">
              <span className="text-sm text-gray-600" data-id="element-70">Estimated Value</span>
              <span className="text-lg font-bold text-au-green" data-id="element-71">
                {bid.estimatedValue}
              </span>
            </div>
          </div>}
      </Card>
    </motion.div>;
}